import type { TransportSelectorContext, TransportSelectorFilters } from '../types'
import type { TransportSelectorUrlBuilder } from '../urlBuilder/transportSelectorUrlBuilder'
import type { FilterSelection } from '@/scopes/prices/domain/filters/types'
import defaultFetch from 'isomorphic-unfetch'
import { createBrowserQueryString } from '@/scopes/common/infrastructure/query-strings/browserQueryString'
import { BrowserQueryString } from '@/scopes/common/infrastructure/query-strings/types/browserQueryString'
import { BusSelectorMappedResponse } from '@/scopes/prices/domain/transports/types'
import {
  BusSelectorResponseMapper,
  createBusSelectorResponseMapper,
} from '@/scopes/prices/infrastructure/transport/bus/busSelectorResponseMapper'
import { createTransportSelectorUrlBuilder, TransportType } from '../urlBuilder/transportSelectorUrlBuilder'

interface Dependencies {
  fetch: typeof defaultFetch
  busSelectorResponseMapper: BusSelectorResponseMapper
  transportSelectorUrlBuilder: TransportSelectorUrlBuilder
  browserQuery: BrowserQueryString
}

export interface BusSelectorApiClient {
  getBuses: (
    criteria: TransportSelectorContext & { filterSelection: FilterSelection } & TransportSelectorFilters
  ) => Promise<BusSelectorMappedResponse>
}

export function createBusSelectorApiClient(dependencies: Partial<Dependencies> = {}) {
  const {
    fetch = defaultFetch,
    busSelectorResponseMapper = createBusSelectorResponseMapper(),
    browserQuery = createBrowserQueryString(),
    transportSelectorUrlBuilder = createTransportSelectorUrlBuilder({ browserQuery }),
  } = dependencies as Dependencies

  const fetchOptions: RequestInit = {
    referrerPolicy: 'no-referrer-when-downgrade',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
    },
  }

  async function getBuses(
    criteria: TransportSelectorContext & { filterSelection: FilterSelection } & TransportSelectorFilters,
  ) {
    const url = transportSelectorUrlBuilder.buildUrl(criteria, TransportType.Bus)

    const response = await fetch(url, fetchOptions)

    const data = (await response.json())

    return busSelectorResponseMapper.map(data)
  }

  return { getBuses }
}
